import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import { selectThreadAppState, executeSophiaAction } from "../features/thread/threadSlice";

interface CatalogSchemaEntry {
  dtype: string;
  enum?: string[];
  [key: string]: unknown;
}

interface CatalogPageMeta {
  total: number;
  page: number;
  page_size: number;
  pages: number;
  [key: string]: unknown;
}

interface CatalogPageResponse {
  data?: Record<string, unknown>[];
  meta?: CatalogPageMeta | Record<string, unknown>;
  schema?: Record<string, CatalogSchemaEntry> | Record<string, unknown>;
  [key: string]: unknown;
}

interface SophiaListProductsResponse {
  'global-page-data'?: {
    products?: CatalogPageResponse;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface SortRow {
  id: number;
  column: string;
  direction: 'asc (+)' | 'desc (-)';
}

interface FilterRow {
  id: number;
  column: string;
  operator: string;
  value: string;
}

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddProducts: (skus: string[], constraints?: Record<string, { lb?: number; ub?: number }>) => void;
  existingSkus?: string[];
  isOptimizationMode?: boolean;
}

const DEFAULT_PAGE_SIZE = 25;

function normaliseSchema(value: unknown): Record<string, CatalogSchemaEntry> {
  if (!value || typeof value !== 'object') {
    return {};
  }
  const entries = value as Record<string, unknown>;
  const schema: Record<string, CatalogSchemaEntry> = {};
  Object.entries(entries).forEach(([key, entry]) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }
    const record = entry as Record<string, unknown>;
    const dtype = typeof record.dtype === 'string' ? record.dtype : 'string';
    const enumValues = Array.isArray(record.enum)
      ? (record.enum as unknown[]).filter((item): item is string => typeof item === 'string')
      : undefined;
    schema[key] = {
      ...record,
      dtype,
      ...(enumValues && enumValues.length ? { enum: enumValues } : {}),
    } as CatalogSchemaEntry;
  });
  return schema;
}

function normaliseMeta(value: unknown): CatalogPageMeta | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  const total = Number(record.total ?? 0);
  const page = Number(record.page ?? 1);
  const pageSize = Number(record.page_size ?? DEFAULT_PAGE_SIZE);
  const pages = Number(record.pages ?? 1);
  const meta: CatalogPageMeta = {
    ...record,
    total: Number.isFinite(total) ? total : 0,
    page: Number.isFinite(page) ? page : 1,
    page_size: Number.isFinite(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    pages: Number.isFinite(pages) ? pages : 1,
  };
  return meta;
}

function buildCloudinaryImageFromPath(fullPath: string | null | undefined, width: number, height: number): string | null {
  if (!fullPath) {
    return null;
  }
  return `https://media.brunellocucinelli.com/image/upload/c_fill,w_${width},h_${height},g_center/${fullPath}`;
}

export function AddProductModal({ isOpen, onClose, onAddProducts, existingSkus = [], isOptimizationMode = false }: AddProductModalProps) {
  const dispatch = useAppDispatch();
  const [schema, setSchema] = useState<Record<string, CatalogSchemaEntry>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<CatalogPageMeta | null>(null);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());
  const [skuConstraints, setSkuConstraints] = useState<Record<string, { lb?: number; ub?: number }>>({});
  const [queryVersion, setQueryVersion] = useState(0);
  const appState = useAppSelector(selectThreadAppState);
  const threadStatus = useAppSelector(state => state.thread.status);
  
  // Convert existingSkus array to Set for faster lookup
  const existingSkusSet = useMemo(() => new Set(existingSkus), [existingSkus]);

  // Get optimization parameters from app state
  const optimSeason = appState['optim-season'] as string | undefined;
  const optimGender = appState['optim-gender'] as string | undefined;
  const optimConsegna = appState['optim-consegna'] as string | undefined;
  const optimSection = appState['optim-section'] as string | undefined;

  const runListProductsAction = useCallback(
    async (actionName: string, params?: Record<string, unknown>) => {
      await dispatch(
        executeSophiaAction({
          actionName,
          params,
          refreshThread: false, // Don't refresh thread in modal to avoid re-opening
        }),
      ).unwrap();
    },
    [dispatch],
  );

  // Apply filters when modal opens
  const [filtersApplied, setFiltersApplied] = useState(false);
  
  useEffect(() => {
    if (!isOpen) {
      setFiltersApplied(false);
      return;
    }
    
    if (threadStatus !== 'succeeded') {
      return;
    }

    async function applyFilters() {
      try {
        setLoading(true);
        
        // Build filter tokens array like in ListProducts.tsx
        const filterTokens: string[] = [];
        
        if (optimSeason) {
          filterTokens.push(`COD_STAGIONE|eq|${optimSeason}`);
        }
        
        if (optimGender) {
          filterTokens.push(`GENDER|eq|${optimGender}`);
        }

        console.log('Applying filters:', filterTokens);
        console.log('Optimization params:', { optimSeason, optimGender, optimConsegna, optimSection });

        // Always apply filters as an array (even if empty)
        await runListProductsAction('list-products-set-filter', {
          'list-products-filter': filterTokens,
        });

        // Reset to page 1
        await runListProductsAction('list-products-set-page', {
          'list-products-page': 1,
        });
        
        setFiltersApplied(true);
        // Trigger a reload after filters are set
        setQueryVersion(version => version + 1);
      } catch (err) {
        console.error('Error applying filters:', err);
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    }

    void applyFilters();
  }, [isOpen, threadStatus, optimSeason, optimGender, optimConsegna, optimSection, runListProductsAction]);

  // Load page data
  useEffect(() => {
    if (!isOpen || threadStatus !== 'succeeded') {
      return;
    }
    
    async function loadPage() {
      try {
        setLoading(true);
        
        const payload = await dispatch(
          executeSophiaAction({
            actionName: 'global-get-page-data',
            params: { 'global-page-name': 'list-products-optimization' },
            refreshThread: false,
          }),
        ).unwrap();
        const response = payload.data as SophiaListProductsResponse;
        const products = response?.['global-page-data']?.products;
        const nextSchema = normaliseSchema(response?.['global-page-data']?.schema);
        
        console.log('Products response:', products);
        console.log('Meta:', products?.meta);
        console.log('Data rows count:', products?.data?.length);
        
        setSchema(nextSchema);
        
        const metaData = normaliseMeta(products?.meta);
        setMeta(metaData);
        
        if (metaData) {
          setPage(metaData.page);
        }
        
        const dataRows = Array.isArray(products?.data)
          ? (products?.data as Record<string, unknown>[])
          : [];
        
        console.log('Setting rows:', dataRows.length);
        setRows(dataRows);
        setError(null);
      } catch (err) {
        console.error('Error loading page:', err);
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    }
    
    void loadPage();
  }, [dispatch, threadStatus, queryVersion, isOpen]);

  const applySearch = useCallback(async () => {
    const nextPage = 1;
    setPage(nextPage);
    setError(null);

    try {
      if (searchTerm && searchTerm.trim()) {
        await runListProductsAction('list-products-set-search', {
          'list-products-search': searchTerm.trim(),
        });
      } else {
        await runListProductsAction('list-products-unset-search');
      }

      await runListProductsAction('list-products-set-page', {
        'list-products-page': nextPage,
      });
    } catch (err) {
      setError((err as Error).message);
      return;
    }

    setQueryVersion(version => version + 1);
  }, [searchTerm, runListProductsAction]);

  const changePage = useCallback(
    async (nextPage: number) => {
      const target = Math.max(1, nextPage);
      setError(null);
      try {
        await runListProductsAction('list-products-set-page', {
          'list-products-page': target,
        });
        setPage(target);
        setQueryVersion(version => version + 1);
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [runListProductsAction],
  );

  const handlePrev = useCallback(() => {
    const target = Math.max(1, page - 1);
    if (target === page) return;
    void changePage(target);
  }, [changePage, page]);

  const handleNext = useCallback(() => {
    const totalPages = meta?.pages ?? page;
    const target = Math.min(totalPages, page + 1);
    if (target === page) return;
    void changePage(target);
  }, [changePage, meta?.pages, page]);

  const toggleSelection = useCallback((sku: string) => {
    setSelectedSkus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sku)) {
        newSet.delete(sku);
      } else {
        newSet.add(sku);
      }
      return newSet;
    });
  }, []);

  const handleAddSelected = useCallback(() => {
    const selectedSkuArray = Array.from(selectedSkus);
    
    if (isOptimizationMode) {
      // In optimization mode, pass constraints for selected SKUs
      const constraintsToPass: Record<string, { lb?: number; ub?: number }> = {};
      selectedSkuArray.forEach(sku => {
        if (skuConstraints[sku]) {
          constraintsToPass[sku] = skuConstraints[sku];
        }
      });
      onAddProducts(selectedSkuArray, constraintsToPass);
    } else {
      // In manual edit mode, just pass SKUs
      onAddProducts(selectedSkuArray);
    }
    
    setSelectedSkus(new Set());
    setSkuConstraints({});
    onClose();
  }, [selectedSkus, skuConstraints, isOptimizationMode, onAddProducts, onClose]);

  const handleClose = useCallback(() => {
    setSelectedSkus(new Set());
    setSkuConstraints({});
    onClose();
  }, [onClose]);

  const updateSkuConstraint = useCallback((sku: string, type: 'lb' | 'ub', value: number | undefined) => {
    setSkuConstraints(prev => {
      const newConstraints = { ...prev };
      if (!newConstraints[sku]) {
        newConstraints[sku] = {};
      }
      if (value === undefined) {
        delete newConstraints[sku][type];
        if (Object.keys(newConstraints[sku]).length === 0) {
          delete newConstraints[sku];
        }
      } else {
        newConstraints[sku][type] = value;
      }
      return newConstraints;
    });
  }, []);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-lg w-[90vw] h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-300 p-6 flex items-center justify-between">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-bold text-gray-900">AGGIUNGI PRODOTTI</h2>
            <div className="flex items-center gap-2 text-sm flex-wrap">
              {optimSeason && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-medium">
                  Stagione: {optimSeason}
                </span>
              )}
              {optimGender && (
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded font-medium">
                  Gender: {optimGender}
                </span>
              )}
              {optimConsegna && (
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-medium">
                  Consegna: {optimConsegna}
                </span>
              )}
              {optimSection && (
                <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded font-medium">
                  Macrotema: {optimSection}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search and Controls */}
        <div className="border-b border-gray-200 p-4 flex items-center gap-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                void applySearch();
              }
            }}
            placeholder="Cerca prodotti..."
            className="flex-1 border border-gray-300 px-4 py-2"
          />
          <button
            onClick={applySearch}
            className="bg-black px-6 py-2 text-white font-medium hover:opacity-80"
          >
            CERCA
          </button>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="font-semibold">{selectedSkus.size}</span>
            <span>prodotti selezionati</span>
          </div>
        </div>

        {/* Pagination */}
        <div className="border-b border-gray-200 px-4 py-2 flex items-center justify-between text-sm">
          <span className="text-gray-600">
            Pagina {page} di {meta?.pages ?? 1} • {meta?.total ?? 0} prodotti totali
          </span>
          <div className="flex gap-2">
            <button
              onClick={handlePrev}
              disabled={page <= 1}
              className="px-3 py-1 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              ← Precedente
            </button>
            <button
              onClick={handleNext}
              disabled={page >= (meta?.pages ?? 1)}
              className="px-3 py-1 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              Successiva →
            </button>
          </div>
        </div>

        {/* Product List */}
        <div className="flex-1 overflow-auto p-4">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">Caricamento...</p>
            </div>
          )}
          {error && (
            <div className="flex items-center justify-center h-full">
              <p className="text-red-500">Errore: {error}</p>
            </div>
          )}
          {!loading && !error && rows.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">Nessun prodotto trovato</p>
            </div>
          )}
          {!loading && !error && rows.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {rows.map((row) => {
                const sku = String(row['SKU']);
                const isAlreadyPresent = existingSkusSet.has(sku);
                const isSelected = selectedSkus.has(sku);
                const sketchUrl = row['SKETCH_URL'] as string | undefined;
                const urlImgList = row['URL_IMG_LIST'] as string | undefined;
                
                // Parse image URL
                let imageUrl: string | null = null;
                if (sketchUrl) {
                  imageUrl = buildCloudinaryImageFromPath(sketchUrl, 200, 300);
                } else if (urlImgList) {
                  try {
                    let parsedUrls = urlImgList;
                    if (typeof parsedUrls === 'string' && parsedUrls.startsWith('{') && parsedUrls.endsWith('}') && !parsedUrls.includes(':')) {
                      const urls = parsedUrls.slice(1, -1).split(',').map(s => s.trim()).filter(s => s.length > 0);
                      if (urls.length > 0) {
                        imageUrl = buildCloudinaryImageFromPath(urls[0], 200, 300);
                      }
                    }
                  } catch (e) {
                    // Ignore parse errors
                  }
                }

                return (
                  <div
                    key={sku}
                    className={`border-2 transition-all ${
                      isAlreadyPresent
                        ? 'border-gray-300 bg-gray-100 opacity-60 cursor-not-allowed'
                        : isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <div
                      className={`${!isAlreadyPresent ? 'cursor-pointer' : ''}`}
                      onClick={() => !isAlreadyPresent && toggleSelection(sku)}
                    >
                      {imageUrl && (
                        <div className="aspect-[2/3] bg-gray-100 flex items-center justify-center overflow-hidden">
                          <img
                            src={imageUrl}
                            alt={sku}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="p-3">
                        <div className="font-semibold text-sm mb-1">{sku}</div>
                        <div className="text-xs text-gray-600 truncate">
                          {row['SHORT_DESCR'] ? String(row['SHORT_DESCR']) : ''}
                        </div>
                        {isAlreadyPresent && (
                          <div className="mt-2 flex items-center gap-1 text-gray-600 text-xs font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Già presente
                          </div>
                        )}
                        {!isAlreadyPresent && isSelected && (
                          <div className="mt-2 flex items-center gap-1 text-blue-600 text-xs font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Selezionato
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Constraints section - only shown in optimization mode and when selected */}
                    {isOptimizationMode && isSelected && !isAlreadyPresent && (
                      <div className="border-t border-gray-300 p-3 bg-blue-50" onClick={(e) => e.stopPropagation()}>
                        <div className="text-xs font-semibold text-gray-700 mb-2">VINCOLI (opzionali)</div>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-600 w-16">Min:</label>
                            <input
                              type="number"
                              min="0"
                              value={skuConstraints[sku]?.lb ?? ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                updateSkuConstraint(sku, 'lb', val === '' ? undefined : parseInt(val, 10));
                              }}
                              placeholder="—"
                              className="flex-1 border border-gray-300 px-2 py-1 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-600 w-16">Max:</label>
                            <input
                              type="number"
                              min="0"
                              value={skuConstraints[sku]?.ub ?? ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                updateSkuConstraint(sku, 'ub', val === '' ? undefined : parseInt(val, 10));
                              }}
                              placeholder="—"
                              className="flex-1 border border-gray-300 px-2 py-1 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-300 p-4 flex items-center justify-end gap-4">
          <button
            onClick={handleClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 font-medium hover:bg-gray-100"
          >
            ANNULLA
          </button>
          <button
            onClick={handleAddSelected}
            disabled={selectedSkus.size === 0}
            className="px-6 py-2 bg-black text-white font-medium hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
            style={selectedSkus.size > 0 ? { backgroundColor: '#a5d8ff', color: '#000' } : {}}
          >
            AGGIUNGI {selectedSkus.size > 0 ? `(${selectedSkus.size})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
